import { useMemo, useState, useEffect } from "react";
import { HttpError, useInfiniteList, useTranslation, useInvalidate } from "@refinedev/core";
import AppointmentProfessionalCard, { AppointmentProfessionalCardProps } from "@components/molecules/AppointmentProfessionalCard";
import { Professional, User_Professional_Public } from "@graphql/schema.types";
import { FaTriangleExclamation, FaMagnifyingGlass, FaArrowRotateRight } from "react-icons/fa6";
import { useDebounce } from "hooks/useDebounce";
import { CalendarId } from "core/calendar/state";

type ProfessionalPublicCalendar = Professional & {
    user_professional_public: User_Professional_Public
}

interface ProfessionalSelectionProps {
    onProfessionalSelect: (index: number) => void;
    onProfessionalContinue: () => void;
    selectedProfessionalIndex: number;
    isLoadingCreateEvent: boolean;
    calendarId: CalendarId | null;
    mobile?: boolean;
    onProfessionalsDataChange?: (data: ProfessionalPublicCalendar[]) => void;
}

export const ProfessionalSelection = ({
    onProfessionalSelect,
    onProfessionalContinue,
    selectedProfessionalIndex,
    isLoadingCreateEvent,
    calendarId,
    mobile,
    onProfessionalsDataChange
}: ProfessionalSelectionProps) => {
    const [searchQuery, setSearchQuery] = useState("")
    const debouncedSearchQuery = useDebounce(searchQuery, 300)
    const { translate } = useTranslation()
    const invalidate = useInvalidate()
    const PAGE_SIZE = 10

    const {
        data: professionalsData,
        isLoading,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
    } = useInfiniteList<ProfessionalPublicCalendar, HttpError, ProfessionalPublicCalendar>({
        resource: 'professional',
        pagination: {
            pageSize: PAGE_SIZE,
        },
        filters: [
            {
                field: 'availability_calendar_id',
                operator: 'null',
                value: false,
            },
            {
                field: 'appointments_calendar_id',
                operator: 'null',
                value: false,
            },
            ...(debouncedSearchQuery ? [{
                field: 'user_professional_public.display_name',
                operator: 'contains' as const,
                value: debouncedSearchQuery
            }] : [])
        ],
        meta: {
            fields: [
                'specialty',
                {
                    'professional_specialties': [
                        'name'
                    ]
                },
                'description',
                'availability_calendar_id',
                'appointments_calendar_id',
                'country',
                {
                    'country_info': [
                        'name'
                    ],
                },
                {
                    'user_professional_public': [
                        'display_name',
                        'avatar_url',
                        'email',
                        'id',
                    ]
                },
            ]
        }
    })
    
    const handleRefresh = async () => {
        await refetch()
        await invalidate({
            resource: 'calendar/freeBusy',
            invalidates: ['list'],
            dataProviderName: 'gapi',
        })
    }


    const professionals = useMemo(() => {
        return professionalsData?.pages.flatMap((page) =>
            page.data.map((professional) => {
                const userProfessional = professional?.user_professional_public
                return userProfessional ? {
                    id: userProfessional.id,
                    name: userProfessional.display_name,
                    email: userProfessional.email,
                    description: professional?.description,
                    photo: userProfessional.avatar_url,
                    specialty: [professional?.specialty, ...professional?.professional_specialties?.map(s => s.name)],
                    country: {
                        code: professional?.country,
                        name: professional?.country_info?.name,
                    }
                } : null
            }).filter(help => !!help)
        ) ?? []
    }, [professionalsData?.pages]) as AppointmentProfessionalCardProps[]

    // Extract the raw professional data for the parent component
    const rawProfessionals = useMemo(() => {
        return professionalsData?.pages.flatMap((page) => page.data) ?? []
    }, [professionalsData?.pages])

    // Notify parent component when professionals data changes
    useEffect(() => {
        onProfessionalsDataChange?.(rawProfessionals)
    }, [rawProfessionals, onProfessionalsDataChange])

    return (
        <div className="flex flex-col">
            <div className="join w-full mb-4">
                <input
                    type="text"
                    placeholder={translate("page.appointments.search.placeholder")}
                    className="input input-bordered w-full join-item"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="btn btn-square btn-primary join-item">
                    <FaMagnifyingGlass className={isFetching ? "animate-spin" : ""} />
                </button>
                <button 
                    className="btn btn-square btn-secondary join-item" 
                    onClick={handleRefresh}
                    disabled={isFetching}
                >
                    <FaArrowRotateRight className={isFetching ? "animate-spin" : ""} />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin">
                {isLoading ? (
                    <div className="flex items-center justify-center p-8">
                        <div className="loading loading-spinner loading-lg" />
                    </div>
                ) : professionals.length > 0 ? (
                    <div className="space-y-3 p-3">
                        {professionals.map((prof, i) => (
                            <AppointmentProfessionalCard
                                key={i}
                                {...prof}
                                active={selectedProfessionalIndex === i}
                                onAction={() => onProfessionalSelect(i)}
                                onContinue={onProfessionalContinue}
                                contactActive={!isLoadingCreateEvent && !!calendarId}
                                mobile={mobile}
                            />
                        ))}
                        {hasNextPage && (
                            <button
                                className="btn btn-ghost w-full"
                                onClick={() => fetchNextPage()}
                                disabled={isFetchingNextPage}
                            >
                                {isFetchingNextPage ? (
                                    <div className="loading loading-spinner loading-md" />
                                ) : (
                                    translate("common.load-more")
                                )}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <FaTriangleExclamation className="w-12 h-12 mx-auto mb-4 text-base-content/30" />
                        <p>{translate("page.appointments.no-professionals")}</p>
                    </div>
                )}
            </div>
        </div>
    )
} 